import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ChevronDownIcon, Link2Icon, SearchIcon, XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { moduleBuilderApi } from '@/api/services/module-builder.api';
import { moduleRecordsApi } from '@/api/services/module-records.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { MODULE_BUILDER_QUERY_KEYS } from '@/features/module-builder/constants';
import type { IModuleField } from '@/features/module-builder/types';
import { useDebounce } from '@/hooks/use-debounce';

export interface RecordLookupSelectProps {
    field: IModuleField;
    /** Selected record id ('' = none). */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

const LOOKUP_PAGE_SIZE = 10;

function truncateId(id: string): string {
    return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

/**
 * Searchable record picker for LOOKUP fields. Resolves the related module by
 * name/slug, then searches its records (debounced, first page only). System
 * table lookups (contacts) render a read-only fallback in v1.
 */
export function RecordLookupSelect({ field, value, onChange, disabled }: RecordLookupSelectProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);

    const modulesQuery = useQuery({
        queryKey: MODULE_BUILDER_QUERY_KEYS.modules(),
        queryFn: () => moduleBuilderApi.listModules(),
        staleTime: 30_000,
        enabled: !!field.relatedModuleName,
    });

    const relatedModule = (modulesQuery.data ?? []).find(
        (module) =>
            module.name === field.relatedModuleName || module.slug === field.relatedModuleName,
    );
    const relatedId = relatedModule?.id ?? '';

    const recordsQuery = useQuery({
        queryKey: MODULE_BUILDER_QUERY_KEYS.records(relatedId, {
            search: debouncedSearch,
            limit: LOOKUP_PAGE_SIZE,
        }),
        queryFn: () =>
            moduleRecordsApi.list(relatedId, {
                search: debouncedSearch || undefined,
                limit: LOOKUP_PAGE_SIZE,
            }),
        enabled: open && !!relatedId,
    });

    const selectedQuery = useQuery({
        queryKey: MODULE_BUILDER_QUERY_KEYS.record(relatedId, value),
        queryFn: () => moduleRecordsApi.getById(relatedId, value),
        enabled: !!value && !!relatedId,
        staleTime: 30_000,
        retry: false,
    });

    // System-table lookups (contacts) are managed by their native UIs — keep a
    // read-only placeholder here for v1.
    if (!field.relatedModuleName) {
        return (
            <div className="flex flex-col gap-1">
                <Input
                    value={value}
                    disabled
                    readOnly
                    placeholder={t('recordForm.lookupPlaceholder', 'Select a record')}
                />
                {field.relatedSystemTable === 'contacts' && (
                    <p className="text-xs text-text-tertiary">
                        {t('recordForm.lookupContacts', 'Contact lookup')}
                    </p>
                )}
            </div>
        );
    }

    const records = recordsQuery.data?.items ?? [];
    const selectedLabel = value ? (selectedQuery.data?.displayTitle ?? truncateId(value)) : '';

    const handleSelect = (recordId: string) => {
        onChange(recordId);
        setOpen(false);
        setSearch('');
    };

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearch('');
            }}
            modal
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className="h-9 w-full justify-between gap-2 bg-bg-surface px-3 font-normal"
                >
                    {value ? (
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                            <Link2Icon className="size-4 shrink-0 text-text-tertiary" />
                            <span className="truncate text-text-primary">{selectedLabel}</span>
                        </span>
                    ) : (
                        <span className="flex min-w-0 flex-1 items-center gap-2 text-text-tertiary">
                            <Link2Icon className="size-4 shrink-0" />
                            <span className="truncate">
                                {field.placeholder ??
                                    t('recordForm.lookupPlaceholder', 'Select a record')}
                            </span>
                        </span>
                    )}
                    <span className="flex shrink-0 items-center gap-1">
                        {value && !disabled && (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label={t('recordForm.clearSelection', 'Clear selection')}
                                className="rounded-sm text-text-tertiary hover:text-text-primary"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onChange('');
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onChange('');
                                    }
                                }}
                            >
                                <XIcon className="size-4" />
                            </span>
                        )}
                        <ChevronDownIcon
                            className={cn(
                                'size-4 text-text-tertiary transition-transform',
                                open && 'rotate-180',
                            )}
                            aria-hidden="true"
                        />
                    </span>
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className="w-(--radix-popover-trigger-width) min-w-64 gap-0 p-0"
                align="start"
                sideOffset={6}
            >
                <div className="border-b border-border-default px-1">
                    <Input
                        startIcon={<SearchIcon />}
                        placeholder={t('recordForm.searchRecords', 'Search records…')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className="h-8 rounded-none border-0 shadow-none focus-visible:shadow-none focus-visible:border-0"
                        data-size="sm"
                    />
                </div>
                <div className="max-h-60 overflow-y-auto overscroll-contain p-1">
                    {modulesQuery.isSuccess && !relatedId ? (
                        <div className="py-4 text-center text-sm text-text-tertiary">
                            {t('recordForm.lookupModuleMissing', 'Related module not found.')}
                        </div>
                    ) : recordsQuery.isLoading || modulesQuery.isLoading ? (
                        <div className="flex justify-center py-4">
                            <Spinner />
                        </div>
                    ) : recordsQuery.isError || modulesQuery.isError ? (
                        <div className="py-4 text-center text-sm text-text-tertiary">
                            {t('recordForm.recordsError', 'Failed to load records.')}
                        </div>
                    ) : records.length === 0 ? (
                        <div className="py-4 text-center text-sm text-text-tertiary">
                            {t('recordForm.noRecords', 'No records found.')}
                        </div>
                    ) : (
                        records.map((record) => {
                            const isSelected = record.id === value;
                            return (
                                <Button
                                    key={record.id}
                                    type="button"
                                    variant="ghost"
                                    onClick={() => handleSelect(record.id)}
                                    className={cn(
                                        'flex h-auto w-full cursor-default items-center justify-start gap-2.5 rounded-sm px-2 py-2 text-sm font-normal',
                                        isSelected &&
                                            'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300',
                                    )}
                                >
                                    <span className="flex-1 truncate text-left text-text-primary">
                                        {record.displayTitle ?? truncateId(record.id)}
                                    </span>
                                    {isSelected && (
                                        <CheckIcon className="size-4 shrink-0 text-primary-500" />
                                    )}
                                </Button>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
